import React from "react";

import api from "../../libs/api";
import { useAssistants } from "./../../redux/assistants";

import closeIcon from "../../assets/images/close.svg";
import infoIcon from "../../assets/images/info.svg";
import avatarIcon from "../../assets/images/avatar.svg";
import styles from "./CancellationNotifications.module.scss";

// Local Storage stores a list of plan cancellation notifications that
// have been dismissed. To make lookup easy, this list is just a simple
// concatenation of 3 fields: "<plan_id>/<status>/<effective_date>".
// For example: "12345/ongoing/2021-05-28"

const statuses = ["completed", "ongoing", "halted"];

function _getAssistantNamesList(assistants) {
  const names = assistants.map(({ name }) => name);
  if (names.length === 1) {
    return names[0];
  } else if (names.length === 2) {
    return names.join(" and ");
  }
  names[names.length - 1] = `and ${names[names.length - 1]}`;
  return names.join(", ");
}

class CancellationNotifications extends React.Component {
  constructor(props) {
    super(props);
    this.state = { cancellations: [], notifications: [] };
  }

  componentDidMount() {
    api.get("/clients/cancel-assistants?recent").then(({ data }) => {
      const cancellations = data || [];
      for (const c of cancellations) {
        c.key = `${c.plan_id}/${c.status}/${c.effective_date}`;
      }
      this.updateNotifications(cancellations);
    });
  }

  updateNotifications(cancellations = this.state.cancellations) {
    // Filter out dismissed notifications
    const dismissedKeys = JSON.parse(
      localStorage.getItem("dismissed-cancel-notifications") || "[]"
    );
    const notifications = cancellations.filter((c) => !dismissedKeys.includes(c.key));
    this.setState({ cancellations, notifications });
  }

  render() {
    // Assume all notifications of the same status have the same effective_date value
    const groupedNotifications = {};
    for (const status of statuses) {
      groupedNotifications[status] = {
        assistants: this.state.notifications
          .filter((n) => n.status === status)
          .map((n) => ({
            name: n.assistant_name,
            plan_id: n.plan_id,
            key: n.key,
          })),
      };
      if (groupedNotifications[status].assistants.length > 0) {
        groupedNotifications[status].effectiveDate = this.state.notifications.find(
          (n) => n.status === status
        ).effective_date;
      }
    }

    return this.state.notifications.length > 0 ? (
      <div className="mb-5 cancellation-notifications-wrapper">
        {statuses.map((status) =>
          groupedNotifications[status].assistants.length > 0 ? (
            <CancellationNotification
              key={status}
              status={status}
              assistants={groupedNotifications[status].assistants}
              effectiveDate={groupedNotifications[status].effectiveDate}
              onDismissed={this.updateNotifications.bind(this)}
            />
          ) : null
        )}
      </div>
    ) : null;
  }
}

const CancellationNotification = ({ status, assistants, effectiveDate, onDismissed }) => {
  const effectiveDateText = new Date(effectiveDate).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const namesList = _getAssistantNamesList(assistants);
  const keyList = assistants.map((a) => a.key).join("|");

  const currentAssistants = useAssistants();
  for (const assistant of assistants) {
    const a = currentAssistants.find((a) => a.plan_id === assistant.plan_id);
    assistant.photo_url = a ? a.photo_url : null;
  }

  function dismissNotification(e) {
    const keys = e.target.parentElement.dataset.keys.split("|");
    const dismissed = JSON.parse(localStorage.getItem("dismissed-cancel-notifications") || "[]");
    dismissed.push(...keys);
    localStorage.setItem("dismissed-cancel-notifications", JSON.stringify(dismissed));
    onDismissed();
  }

  const profilePhotos = (
    <div
      className={
        styles.Photos +
        " " +
        (assistants.length === 2 ? styles.Two : assistants.length > 2 ? styles.Multiple : "")
      }
    >
      {assistants.map((a, idx) =>
        idx < 3 ? (
          <img key={a.key} style={{ zIndex: 3 - idx }} src={a.photo_url || avatarIcon} alt="" />
        ) : null
      )}
    </div>
  );

  if (status === "ongoing") {
    const emailBody = `Hi Magic Team,

Can you help me with my recent work stoppage request?

- Assistant name(s): ${namesList}
- Date when stoppage will be effective: ${effectiveDateText}

[Insert your comments here]`;
    return (
      <div
        className={`mb-2 ${styles.CancelNotification}`}
        data-keys={keyList}
        data-testid="CancelNotification-ongoing"
      >
        <img
          src={closeIcon}
          className={styles.X}
          alt=""
          onClick={dismissNotification}
          aria-hidden="true"
          data-testid="CancelNotification-ongoing-x"
        />
        {profilePhotos}
        <b>
          <img src={infoIcon} className="mr-2" alt="ⓘ" />
          {assistants.length === 1 ? `${assistants[0].name}’s ` : "Multiple "}
          assistant services will be stopped on {effectiveDateText}
        </b>
        <ul>
          <li>
            Work stoppage {assistants.length === 1 ? "" : `for ${namesList}`} will be effective at
            the end of your current billing period on {effectiveDateText}.
          </li>
          <li>Assistant payments for this week will still be processed.</li>
          <li>
            If this is an error please{" "}
            <a
              href={`mailto:client@getmagicnow.com?subject=Assistant%20Work%20Stoppage&body=${encodeURIComponent(
                emailBody
              )}`}
            >
              contact Magic
            </a>{" "}
            as soon as possible.
          </li>
        </ul>
        <button
          type="button"
          className={`btn btn-lilac ${styles.Button}`}
          onClick={dismissNotification}
          data-testid="CancelNotification-ongoing-button"
        >
          Close
        </button>
      </div>
    );
  }

  if (status === "halted")
    return (
      <div
        className={`mb-2 ${styles.CancelNotification}`}
        data-keys={keyList}
        data-testid="CancelNotification-halted"
      >
        <img
          src={closeIcon}
          className={styles.X}
          alt=""
          onClick={dismissNotification}
          aria-hidden="true"
          data-testid="CancelNotification-halted-x"
        />
        {profilePhotos}
        <b>
          <img src={infoIcon} className="mr-2" alt="ⓘ" />
          {assistants.length === 1
            ? `${assistants[0].name}’s work stoppage request was`
            : "Multiple work stoppage requests were"}{" "}
          halted. Assistant services will resume.
        </b>
        <ul>
          <li>
            You halted{" "}
            {assistants.length === 1
              ? `${assistants[0].name}’s work stoppage request`
              : "multiple work stoppage requests"}
            .
          </li>
          <li>
            {assistants.length === 1
              ? `${assistants[0].name}’s assistant services`
              : `Assistant services from ${namesList}`}{" "}
            will resume as normal.
          </li>
          <li>
            Questions? <a href="mailto:client@getmagicnow.com">Contact Magic</a>.
          </li>
        </ul>
        <button
          type="button"
          className={`btn btn-lilac ${styles.Button}`}
          onClick={dismissNotification}
          data-testid="CancelNotification-halted-button"
        >
          Close
        </button>
      </div>
    );

  // completed
  return (
    <div
      className={`mb-2 ${styles.CancelNotification}`}
      data-keys={keyList}
      data-testid="CancelNotification-completed"
    >
      <img
        src={closeIcon}
        className={styles.X}
        alt=""
        onClick={dismissNotification}
        aria-hidden="true"
        data-testid="CancelNotification-completed-x"
      />
      {profilePhotos}
      <b>
        <img src={infoIcon} className="mr-2" alt="ⓘ" />
        {assistants.length === 1 ? `${assistants[0].name}’s` : "Multiple"} assistant services were
        stopped on {effectiveDateText}
      </b>
      <ul>
        {assistants.length === 1 ? "" : <li>Work stoppage for {namesList} are now in effect.</li>}
        <li>
          {assistants.length === 1 ? `${assistants[0].name} has` : "These assistants have"} been
          removed from your portal.
        </li>
        <li>
          You still have access to <a href="#billing">billing history</a> for tax purposes.
        </li>
        <li>
          Questions? <a href="mailto:client@getmagicnow.com">Contact Magic</a>.
        </li>
      </ul>
      <button
        type="button"
        className={`btn btn-lilac ${styles.Button}`}
        onClick={dismissNotification}
        data-testid="CancelNotification-completed-button"
      >
        Close
      </button>
    </div>
  );
};

export default CancellationNotifications;
